<template>
  <MRow
    :gutter="0"
    :class="{
      'bg-neutral-lightest11': isDone && markBgColor,
      'task-closed': isDone && markBgColor,
    }"
    class="task-item"
  >
    <MCol>
      <MRow>
        <MCol>
          <div class="heading flex flex-1 items-center">
            <div class="flex-1">
              <h5
                v-if="allowTaskHeader"
                class="text-primary flex items-start flex-1"
              >
                {{ task.name }}: {{ task.subject }}
              </h5>
            </div>
            <div class="flex items-end">
              <MTooltip
                v-if="
                  allowEdit &&
                  !disabled &&
                  !isDone &&
                  canUpdateTask(task.moduleName)
                "
              >
                <template v-slot:trigger>
                  <FlotoAnchorLink
                    :aria-label="$t('edit')"
                    class="text-neutral"
                    @click="$emit('edit', task)"
                  >
                    <MIcon
                      :id="`edit-btn-${task.name}:${task.subject}`"
                      name="pencil"
                      class="text-neutral"
                    />
                  </FlotoAnchorLink>
                </template>
                {{ $t('edit') }}
              </MTooltip>
              <FlotoDeleteBtn
                v-if="allowTaskDeleteAction && canArchiveTask(task.moduleName)"
                :id="`delete-btn-${task.name}:${task.subject}`"
                class="mx-1"
                :message="$t('confirm_delete_item', { item: $tc('task') })"
                @confirm="$emit('remove')"
              />
              <a v-if="isDone" class="cursor-default ml-2">
                <div class="bg-secondary-green p-1 rounded flex items-center">
                  <MIcon name="check" class="text-white" />
                </div>
              </a>
              <MTooltip
                v-else-if="
                  (overDue !== false || dueIn !== false) && !isInActiveTask
                "
              >
                <template v-slot:trigger>
                  <a class="cursor-default">
                    <div
                      class="p-1 rounded flex items-center ml-2"
                      :class="{
                        'bg-secondary-red': overDue,
                        'bg-secondary-green': !overDue,
                      }"
                    >
                      <MIcon name="clock" class="text-white" />
                    </div>
                  </a>
                </template>
                {{ overDue || dueIn }} <br />
                ({{ task.timeRange.endTime | datetime }})
              </MTooltip>
            </div>
          </div>
        </MCol>
      </MRow>
      <MRow class="mb-3" :gutter="0">
        <MCol v-if="allowedSystemFields.indexOf('statusId') >= 0" :size="3">
          <FlotoStatusPicker
            :value="task.statusId"
            module-name="task"
            :disabled="
              isDisabledUserToReopenClosedTasks ||
              isDone ||
              !canUpdateTaskStatus(task.moduleName) ||
              singleFieldRulesStateFn('statusId').disable
            "
            :allowed-status="
              isProjectMilestoneTask ? ['open', 'closed', 'cancelled'] : []
            "
            @change="handleChange({ statusId: $event })"
          >
            <template v-slot:trigger="slotData">
              <FlotoDropdownTrigger
                type="secondary"
                v-bind="slotData"
                :lable="$t('status')"
                icon-name="status"
                :disabled="
                  disabled ||
                  isDone ||
                  !canUpdateTaskStatus(task.moduleName) ||
                  singleFieldRulesStateFn('statusId').disable
                "
              />
            </template>
          </FlotoStatusPicker>
        </MCol>
        <MCol v-if="allowedSystemFields.indexOf('priorityId') >= 0" :size="3">
          <FlotoPriorityPicker
            :value="task.priorityId"
            :disabled="
              disabled ||
              detailsDisabled ||
              isDone ||
              !canUpdateTaskPriority(task.moduleName) ||
              singleFieldRulesStateFn('priorityId').disable
            "
            @change="handleChange({ priorityId: $event })"
          >
            <template v-slot:trigger="slotData">
              <FlotoDropdownTrigger
                type="secondary"
                v-bind="slotData"
                :lable="$t('priority')"
                icon-name="priority"
                :disabled="
                  disabled ||
                  detailsDisabled ||
                  isDone ||
                  !canUpdateTaskPriority(task.moduleName) ||
                  singleFieldRulesStateFn('priorityId').disable
                "
              />
            </template>
          </FlotoPriorityPicker>
        </MCol>
        <MCol v-if="allowedSystemFields.indexOf('taskTypeId') >= 0" :size="3">
          <!-- <MIcon name="file-check" class="mr-4 text-neutral-light" size="2x" /> -->
          <FlotoTaskTypePicker
            :value="task.taskTypeId"
            :disabled="
              disabled ||
              isProjectMilestoneTask ||
              detailsDisabled ||
              isDone ||
              !canUpdateTask(task.moduleName) ||
              singleFieldRulesStateFn('taskTypeId').disable
            "
            @change="handleChange({ taskTypeId: $event })"
          >
            <template v-slot:trigger="slotData">
              <FlotoDropdownTrigger
                type="secondary"
                v-bind="slotData"
                :lable="`${$tc('task_type')}`"
                icon-name="file-check"
                :disabled="
                  disabled ||
                  isProjectMilestoneTask ||
                  detailsDisabled ||
                  isDone ||
                  !canUpdateTask(task.moduleName) ||
                  singleFieldRulesStateFn('taskTypeId').disable
                "
              />
            </template>
          </FlotoTaskTypePicker>
        </MCol>
        <MCol v-if="allowedSystemFields.indexOf('groupId') >= 0" :size="3">
          <component
            :is="
              task.moduleName === $constants.PROJECT &&
              hasProjectRequesterLicense
                ? 'FlotoUserGroupPicker'
                : 'FlotoTechnicianGroupPicker'
            "
            :value="task.groupId"
            :disabled="
              disabled ||
              detailsDisabled ||
              isDone ||
              !canUpdateTask(task.moduleName) ||
              singleFieldRulesStateFn('groupId').disable
            "
            @change="handleChange({ groupId: $event })"
          >
            <template v-slot:trigger="slotData">
              <FlotoDropdownTrigger
                type="secondary"
                v-bind="slotData"
                :lable="`${$tc('user')} ${$tc('group')}`"
                icon-name="users"
                :disabled="
                  disabled ||
                  detailsDisabled ||
                  isDone ||
                  !canUpdateTask(task.moduleName) ||
                  singleFieldRulesStateFn('groupId').disable
                "
              />
            </template>
          </component>
        </MCol>
      </MRow>
      <MRow class="mb-3" :gutter="0">
        <MCol v-if="allowedSystemFields.indexOf('technicianId') >= 0" :size="3">
          <component
            :is="
              task.moduleName === $constants.PROJECT &&
              hasProjectRequesterLicense
                ? 'FlotoRequesterPicker'
                : 'FlotoTechnicianPicker'
            "
            :value="task.technicianId"
            :size="35"
            :disabled="
              disabled ||
              detailsDisabled ||
              isDone ||
              !canAssignTask(task.moduleName) ||
              singleFieldRulesStateFn('technicianId').disable
            "
            keep-unassigned
            :group-id="task.groupId"
            change-technician-on-group-change
            @change="handleChange({ technicianId: $event })"
          >
            <template v-slot:trigger="slotData">
              <FlotoDropdownTrigger
                type="secondary"
                v-bind="slotData"
                :lable="`${$tc('assignee')}`"
                icon-name="vector"
                :disabled="
                  disabled ||
                  detailsDisabled ||
                  isDone ||
                  !canAssignTask(task.moduleName) ||
                  singleFieldRulesStateFn('technicianId').disable
                "
                style="margin-right: 0"
              >
                <template v-slot:icon>
                  <FlotoUserAvatar
                    :size="25"
                    :avatar="(slotData.currentItem || {}).avatar"
                    :style="{
                      minWidth: `${24}px`,
                    }"
                  />
                </template>
              </FlotoDropdownTrigger>
            </template>
          </component>
        </MCol>
        <MCol
          v-if="
            allowedSystemFields.indexOf('startTime') >= 0 ||
            allowedSystemFields.indexOf('endTime') >= 0
          "
          :size="6"
        >
          <FlotoTriggerView
            icon-name="calendar-alt"
            type="secondary"
            :lable="`${
              allowedSystemFields.indexOf('startTime') >= 0 ? $t('start') : ''
            } ${$t('date')} ${
              allowedSystemFields.indexOf('startTime') >= 0 &&
              allowedSystemFields.indexOf('endTime') >= 0
                ? ' - '
                : ''
            } ${
              allowedSystemFields.indexOf('endTime') >= 0 ? $t('end') : ''
            } ${$t('date')}`"
          >
            <template v-slot:value>
              <div class="">
                <template
                  v-if="
                    (task.timeRange || {}).startTime ||
                    (task.timeRange || {}).endTime
                  "
                >
                  <span v-if="allowedSystemFields.indexOf('startTime') >= 0">{{
                    (task.timeRange || {}).startTime | datetime
                  }}</span>
                  <span
                    v-if="
                      (task.timeRange || {}).startTime &&
                      (task.timeRange || {}).endTime &&
                      allowedSystemFields.indexOf('endTime') >= 0 &&
                      allowedSystemFields.indexOf('startTime') >= 0
                    "
                    class="text-center my-1"
                  >
                    -
                  </span>
                  <span v-if="allowedSystemFields.indexOf('endTime') >= 0">{{
                    task.timeRange.endTime | datetime
                  }}</span>
                </template>
                <template v-else> --- </template>
              </div>
            </template>
          </FlotoTriggerView>
        </MCol>
      </MRow>
      <MRow
        v-if="task.moduleName === $constants.PROJECT"
        class="mb-3"
        :gutter="0"
      >
        <MCol v-if="allowedSystemFields.indexOf('completion') >= 0" :size="3">
          <FlotoTriggerView
            icon-name="check-circle"
            type="secondary"
            :lable="`${$t('completion')}`"
            :value="`${task.completion} %`"
          />
        </MCol>
        <MCol v-if="allowedSystemFields.indexOf('duration') >= 0" :size="3">
          <FlotoTriggerView
            icon-name="clock"
            type="secondary"
            :lable="`${$t('duration')} (${$tc('days', 2)})`"
            :value="`${task.duration} ${$tc('days', task.duration)}`"
          />
        </MCol>
        <MCol
          v-if="allowedSystemFields.indexOf('estimatedTime') >= 0"
          :size="3"
        >
          <FlotoTriggerView
            icon-name="hourglass"
            type="secondary"
            :lable="`${$t('estimates')} (${$tc('hours', 2)})`"
            :value="
              task.estimatedTime
                ? `${task.estimatedTime} ${$tc('hours', 2)}`
                : '---'
            "
          />
        </MCol>
      </MRow>
      <slot name="task-actions">
        <MRow>
          <MCol>
            <template v-if="!disabled">
              <FlotoDeleteBtn
                v-if="!isDone"
                placement="bottomLeft"
                :disabled="detailsDisabled || !canCloseTask(task.moduleName)"
                :message="$t('confirm', { message: $t('task_is_done') })"
                :show-tooltip="false"
                @confirm="handleChangeStatus('closed')"
              >
                <MButton
                  id="done-btn"
                  class="mr-2"
                  :disabled="detailsDisabled || !canCloseTask(task.moduleName)"
                >
                  {{ $t('done') }}
                </MButton>
              </FlotoDeleteBtn>
              <MButton
                v-else
                id="reopen-btn"
                class="mr-2"
                :disabled="
                  isDisabledUserToReopenClosedTasks ||
                  !canReopenTask(task.moduleName)
                "
                @click="handleChangeStatus('open')"
              >
                {{ $t('reopen') }}
              </MButton>
            </template>
            <FlotoTaskDrawer
              v-if="!isInActiveTask"
              :default-task="task"
              :disabled="disabled"
              :details-disabled="detailsDisabled"
              :parent-id="task.parentId"
              :module-name="task.moduleName"
              :hide-conversation="isInActiveTask"
              :hide-work="isInActiveTask"
              @remove="$emit('remove', task)"
              @update="$emit('update', $event)"
            >
              <template v-slot:trigger="{ open }">
                <MButton
                  id="view-details-btn"
                  variant="default"
                  class="mr-2"
                  @click="open"
                >
                  {{ $t('view') }} {{ $t('details') }}
                </MButton>
              </template>
            </FlotoTaskDrawer>
          </MCol>
        </MRow>
      </slot>
    </MCol>
  </MRow>
</template>

<script>
import Pick from 'lodash/pick'
import Moment from 'moment'
import { StatusComputed } from '@state/modules/status'
import { TaskComputed } from '@state/modules/task'
import { PreferenceComputed } from '@state/modules/preference'
import { taskPermissionsMixin } from '@src/mixins/task-permissions'
import { LicenseComputed } from '@state/modules/license'
import { FormComputed } from '@state/modules/form'
import { authComputed } from '@state/modules/auth'
import { getFieldByRequesterAccess } from '@modules/support-portal/helpers/field-access'
export default {
  name: 'TaskDetails',
  mixins: [taskPermissionsMixin],
  inject: {
    formRulesContext: {
      default: () => ({
        fieldRulesState: {},
      }),
    },
  },
  props: {
    task: { type: Object, required: true },
    markBgColor: { type: Boolean, default: false },
    allowEdit: { type: Boolean, default: false },
    // eslint-disable-next-line
    allowTaskHeader: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    allowDelete: { type: Boolean, default: false },
    isInActiveTask: { type: Boolean, default: false },
    detailsDisabled: { type: Boolean, default: false },
  },
  computed: {
    ...StatusComputed,
    ...TaskComputed,
    ...FormComputed,
    ...authComputed,
    ...Pick(PreferenceComputed, [
      'allowUserToReopenClosedTasks',
      'allowUserToDeleteTasks',
    ]),
    ...Pick(LicenseComputed, ['hasProjectRequesterLicense']),
    allowedSystemFields() {
      let fields = this.taskFields({
        exclude: [],
      })

      if (this.isPortalLogin) {
        fields = getFieldByRequesterAccess(this.taskPortalFields, this.user)
      }

      return fields.filter((f) => f.isSystemField).map((f) => f.paramName)
    },
    isProjectMilestoneTask() {
      const taskTypeId = (
        this.taskTypeOptions.find((t) => t.systemName === 'Milestone') || {}
      ).id
      if (
        this.task.moduleName === this.$constants.PROJECT &&
        this.task.taskTypeId === taskTypeId
      ) {
        return true
      }
      return false
    },
    isDone() {
      const closedStatus = this.taskFindStatus('closed')
      if (closedStatus) {
        return closedStatus.id === this.task.statusId
      }
      return false
    },
    overDue() {
      if (this.isDone) {
        return false
      }
      if (!(this.task.timeRange || {}).endTime) {
        return false
      }
      const diff = Moment().unix() * 1000 - this.task.timeRange.endTime
      if (diff > 0) {
        // return `${this.$t('overdue')} ${this.$t('by')} ${Moment.duration(
        //   diff,
        //   'millisecond'
        // ).humanize()}`
        return `${this.$t('overdue')} ${this.$t(
          'by'
        )} ${this.$options.filters.duration(diff)}`
      }
      return false
    },
    dueIn() {
      if (this.isDone) {
        return false
      }
      if (!(this.task.timeRange || {}).endTime) {
        return false
      }
      const diff = Math.abs(
        Moment().unix() * 1000 - this.task.timeRange.endTime
      )
      // return `${this.$t('due_in')} ${Moment.duration(
      //   diff,
      //   'millisecond'
      // ).humanize()}`
      return `${this.$t('due_in')} ${this.$options.filters.duration(diff)}`
    },
    isDisabledUserToReopenClosedTasks() {
      const currentTask = this.taskStatus.find(
        (f) => f.key === this.task.statusId
      )
      if (this.disabled) {
        return true
      }
      if (this.detailsDisabled) {
        return true
      }
      if (
        currentTask &&
        !this.allowUserToReopenClosedTasks &&
        (currentTask.systemName || '').toLowerCase() === 'closed'
      ) {
        return true
      }
      return false
    },
    allowTaskDeleteAction() {
      if (this.allowUserToDeleteTasks) {
        return !this.disabled && this.allowDelete
      }
      return false
    },
  },
  methods: {
    handleChange(change) {
      this.$emit('update', { ...this.task, ...change })
    },
    handleChangeStatus(status) {
      const change = {
        statusId: this.taskFindStatus(status).id,
      }
      this.handleChange(change)
    },
    singleFieldRulesStateFn(paramName) {
      if (!paramName) {
        return {}
      }
      const fields = this.taskForm.fields
      const currentField = (fields || []).find((f) => f.paramName === paramName)
      if (this.isPortalLogin) {
        const prepairedRuleState = {
          ...(this.formRulesContext.fieldRulesState || {}),
          [paramName]: {
            ...((this.formRulesContext.fieldRulesState || {})[paramName] || {}),
            disable:
              !(currentField || {}).requesterCanEdit ||
              ((this.formRulesContext.fieldRulesState || {})[paramName] || {})
                .disable,
          },
        }
        return (prepairedRuleState || {})[paramName] || {}
      } else {
        return (this.formRulesContext.fieldRulesState || {})[paramName] || {}
      }
    },
  },
}
</script>

<style lang="less" scoped>
.heading {
  position: relative;
  padding-right: 0;

  .mark-icon {
    position: absolute;
    top: 0;
    right: 5px;
  }
}

.task-item {
  // background: var(--page-background-color);
  // border: 1px solid var(--border-color);
  // &.task-closed {
  //   border: 1px solid var(--secondary-green);
  // }
}
</style>
